import { action } from 'typesafe-actions';
import { ConnectIntegrationType } from 'hooks/useConnectIntegration';
import { ICurrentEmployee } from 'store/types';
import {
  ChannelType,
  IAuthMFARequired,
  IAuthRequest,
  IAuthResendVerificationCode,
  IAuthToken,
  ICardSession,
  IForgotPasswordRequest,
  IForgotPasswordVerifyRequest,
  IForgotPasswordVerifyToken,
  IGoogleUser,
  IGoogleUserRequest,
  IIdentity,
  IResetPassword,
  IResetPasswordRequest,
} from 'types';
import * as ActionTypes from './types';

// Current Profile, Site & Date Range
// Switching profile (another org, or yourself) clears state scoped to the previous one; setting the site keeps it.
export const switchProfile = (profile?: ICurrentEmployee) => action(ActionTypes.App.SWITCH_PROFILE, profile);
export const setCurrentSite = (siteId?: string) => action(ActionTypes.App.SET_CURRENT_SITE, { siteId });
export const setCurrentDateRange = (startDate: string, endDate: string) => action(ActionTypes.App.SET_CURRENT_DATE_RANGE, { startDate, endDate });

// Login
export const loginRequest = (body: IAuthRequest, onMFARequired: (mfa: IAuthMFARequired) => void, onFail?: (error: any) => void, integration?: ConnectIntegrationType) =>
  action(ActionTypes.App.LOGIN_REQUEST, { body, onMFARequired, onFail, integration });
export const loginSuccess = (response: IAuthToken) => action(ActionTypes.App.LOGIN_SUCCESS, response);
export const loginMFARequired = (response: IAuthMFARequired) => action(ActionTypes.App.LOGIN_SUCCESS_MFA_REQUIRED, response);
export const loginFail = (error: any, failToast?: boolean) => action(ActionTypes.App.LOGIN_FAIL, error, { failToast });

// Log Out
export const logOutRequest = (redirectUrl?: string) => action(ActionTypes.App.LOGOUT_REQUEST, { redirectUrl });
export const logOutSuccess = () => action(ActionTypes.App.LOGOUT_SUCCESS);
export const logOutFail = (error: any) => action(ActionTypes.App.LOGOUT_FAIL, error);

// Forgot Password
export const forgotPasswordRequest = (body: IForgotPasswordRequest, onSuccess?: () => void, toastMessage?: string) => action(ActionTypes.App.FORGOT_PASSWORD_REQUEST, { body, onSuccess, toastMessage });
export const forgotPasswordSuccess = (response: IIdentity) => action(ActionTypes.App.FORGOT_PASSWORD_SUCCESS, response);
export const forgotPasswordFail = (error: any, failToast?: boolean) => action(ActionTypes.App.FORGOT_PASSWORD_FAIL, error, { failToast });

// Forgot Password Verify
export const forgotPasswordVerifyRequest = (identityId: string, body: IForgotPasswordVerifyRequest, redirectUrl?: string) => action(ActionTypes.App.FORGOT_PASSWORD_VERIFY_REQUEST, { identityId, body, redirectUrl });
export const forgotPasswordVerifySuccess = (response: IForgotPasswordVerifyToken) => action(ActionTypes.App.FORGOT_PASSWORD_VERIFY_SUCCESS, response);
export const forgotPasswordVerifyFail = (error: any, failToast?: boolean) => action(ActionTypes.App.FORGOT_PASSWORD_VERIFY_FAIL, error, { failToast });

// Reset Password
export const resetPasswordRequest = (identityId: string, body: IResetPasswordRequest, toastMessage?: string) => action(ActionTypes.App.RESET_PASSWORD_REQUEST, { identityId, body, toastMessage });
export const resetPasswordSuccess = (response: IResetPassword) => action(ActionTypes.App.RESET_PASSWORD_SUCCESS, response);
export const resetPasswordFail = (error: any, failToast?: boolean) => action(ActionTypes.App.RESET_PASSWORD_FAIL, error, { failToast });

// MFA Verify
export const mfaVerifyRequest = (authId: string, code: string, onSuccess?: () => void) => action(ActionTypes.App.MFA_VERIFY_REQUEST, { authId, code, onSuccess });
export const mfaVerifySuccess = (response: IAuthToken) => action(ActionTypes.App.MFA_VERIFY_SUCCESS, response);
export const mfaVerifyFail = (error: any, failToast?: boolean) => action(ActionTypes.App.MFA_VERIFY_FAIL, error, { failToast });

// MFA Resend
export const mfaResendRequest = (authId: string, channel?: ChannelType) => action(ActionTypes.App.MFA_RESEND_REQUEST, { authId, channel });
export const mfaResendSuccess = (response: IAuthResendVerificationCode) => action(ActionTypes.App.MFA_RESEND_SUCCESS, response);
export const mfaResendFail = (error: any, failToast?: boolean) => action(ActionTypes.App.MFA_RESEND_FAIL, error, { failToast });

// Google User
export const getGoogleUserRequest = (body: IGoogleUserRequest, onSuccess: (googleUser: IGoogleUser) => void) => action(ActionTypes.App.GET_GOOGLE_USER_REQUEST, { body, onSuccess });
export const getGoogleUserSuccess = (response: IGoogleUser) => action(ActionTypes.App.GET_GOOGLE_USER_SUCCESS, response);
export const getGoogleUserFail = (error: any) => action(ActionTypes.App.GET_GOOGLE_USER_FAIL, error);

// Card Session
export const getCardSessionRequest = (hostUri: string) => action(ActionTypes.App.GET_CARD_SESSION_REQUEST, { hostUri });
export const getCardSessionSuccess = (response: ICardSession) => action(ActionTypes.App.GET_CARD_SESSION_SUCCESS, response);
export const getCardSessionFail = (error: any, failToast?: boolean) => action(ActionTypes.App.GET_CARD_SESSION_FAIL, error, { failToast });
export const destroyCardSession = () => action(ActionTypes.App.DESTROY_CARD_SESSION);

// Tip Calcs Form Dirty
export const setIsTipCalcFormDirty = (isDirty: boolean) => action(ActionTypes.App.SET_TIP_CALCULATOR_FORM_DIRTY, { isDirty });
